function formatDossierDate(value) {
    if (value == null || value === '') return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
}

function formatGender(gender) {
    if (gender == null || gender === '') return '';
    const normalized = String(gender).toLowerCase();
    if (normalized === 'male' || normalized === 'm') return 'Мъж';
    if (normalized === 'female' || normalized === 'f') return 'Жена';
    return String(gender);
}

function formatCountry(country) {
    if (country == null || country === '') return '';
    const code = String(country).toUpperCase();
    if (code === 'BG') return 'България';
    return String(country);
}

function mapChildren(api) {
    const list = api?.children ?? api?.kidsList ?? api?.personKids;
    if (Array.isArray(list) && list.length > 0) {
        return list.map((child, index) => ({
            no: child?.no ?? child?.number ?? index + 1,
            name: child?.name ?? child?.fullName ?? '',
            birthDate: formatDossierDate(child?.birthDate ?? child?.dateOfBirth),
        }));
    }
    return [];
}

function mapAdditionalExperienceItems(additionalExpirience) {
    if (additionalExpirience == null) return [];

    const list = Array.isArray(additionalExpirience)
        ? additionalExpirience
        : Array.isArray(additionalExpirience?.items)
            ? additionalExpirience.items
            : Array.isArray(additionalExpirience?.experiences)
                ? additionalExpirience.experiences
                : [];

    return list.map((item) => ({
        startDate: formatDossierDate(item?.fromDate ?? item?.startDate),
        endDate: formatDossierDate(item?.toDate ?? item?.endDate),
        position: item?.position ?? item?.positionName ?? item?.jobName ?? '',
        structuralGroup: item?.structuralGroup ?? item?.orgUnitName ?? '',
        level: item?.level ?? item?.unitLevelName ?? '',
    }));
}

/**
 * Parses salary amount from API (number or string with comma decimal).
 * @returns {number|null}
 */
export function parseSalaryAmount(value) {
    if (value == null || value === '') return null;
    const normalized = String(value).replace(/\s/g, '').replace(',', '.');
    const num = Number(normalized);
    return Number.isFinite(num) ? num : null;
}

/**
 * % change: ((new - previous) / previous) * 100
 * Prefer backend-provided % when available; use this when only old/new amounts exist.
 * @returns {string} e.g. "+12.5%" or "" if not calculable
 */
export function calculateSalaryIncreasePercent(previousSalary, newSalary) {
    const previous = parseSalaryAmount(previousSalary);
    const next = parseSalaryAmount(newSalary);
    if (previous == null || next == null || previous === 0) {
        return '';
    }

    const percent = ((next - previous) / previous) * 100;
    const rounded = Math.round(percent * 100) / 100;
    const sign = rounded > 0 ? '+' : '';
    return `${sign}${rounded}%`;
}

function formatIncreasePercentFromApi(value) {
    if (value == null || value === '') return '';
    const text = String(value).trim();
    if (!text) return '';
    return text.includes('%') ? text : `${text}%`;
}

function resolveIncreasePercent(contract, previousGrossSalary) {
    const fromApi = contract?.increasePercent ?? contract?.percentIncrease ?? contract?.increase;
    if (fromApi != null && fromApi !== '') {
        return formatIncreasePercentFromApi(fromApi);
    }

    const baseSalary =
        contract?.baseSalary
        ?? contract?.oldSalary
        ?? contract?.basicSalary
        ?? previousGrossSalary;
    const grossSalary = contract?.grossSalary ?? contract?.salary ?? contract?.bruttoSalary;

    return calculateSalaryIncreasePercent(baseSalary, grossSalary);
}

function mapSalaryContracts(contracts) {
    if (!Array.isArray(contracts)) return [];

    const sorted = [...contracts].sort((a, b) => {
        const dateA = new Date(a?.fromDate ?? a?.date ?? 0).getTime();
        const dateB = new Date(b?.fromDate ?? b?.date ?? 0).getTime();
        return dateA - dateB;
    });

    let previousGross = null;

    return sorted.map((contract) => {
        const grossRaw = contract?.grossSalary ?? contract?.salary ?? contract?.bruttoSalary ?? '';
        const baseRaw =
            contract?.baseSalary
            ?? contract?.oldSalary
            ?? contract?.basicSalary
            ?? previousGross
            ?? '';

        const row = {
            date: formatDossierDate(contract?.date ?? contract?.fromDate),
            remunerationCode: contract?.remunerationCode ?? contract?.source ?? contract?.code ?? '',
            baseSalary: baseRaw != null && baseRaw !== '' ? String(baseRaw) : '',
            grossSalary: grossRaw != null && grossRaw !== '' ? String(grossRaw) : '',
            currency: contract?.currency ?? '',
            increasePercent: resolveIncreasePercent(contract, previousGross),
            reason: contract?.reason ?? contract?.motive ?? contract?.motiv ?? '',
        };

        const grossNum = parseSalaryAmount(grossRaw);
        if (grossNum != null) {
            previousGross = grossRaw;
        }

        return row;
    });
}

function mapLanguageSkills(languageSkills) {
    if (!Array.isArray(languageSkills)) return [];

    return languageSkills.map((skill) => ({
        language: skill?.language ?? '',
        listening: skill?.listening ?? '',
        reading: skill?.reading ?? '',
        interaction: skill?.spokenInteract ?? skill?.interaction ?? '',
        speaking: skill?.spokenProduction ?? skill?.speaking ?? '',
        writing: skill?.writing ?? '',
    }));
}

function mapEducations(educations) {
    if (!Array.isArray(educations)) return [];

    return educations.map((edu) => ({
        document: edu?.degreeLevel ?? edu?.document ?? '',
        degree: edu?.degreeLevel ?? edu?.degree ?? '',
        specialty: edu?.specialty ?? '',
        graduationDate: formatDossierDate(edu?.graduationDate),
        institution: edu?.hrEducationInst ?? edu?.institution ?? '',
        country: formatCountry(edu?.country),
    }));
}

/**
 * Maps `/emp/file/{id}` response to `generateDossierPreviewPdf` dossierData shape.
 * @param {Object} api
 */
export function mapEmpFileToDossierData(api) {
    if (!api || typeof api !== 'object') {
        return {};
    }

    const children = mapChildren(api);
    const kidsCount = api.kids != null ? String(api.kids) : String(children.length);

    return {
        photoUrl: api.photoUrl ?? api.photo ?? api.personPhoto ?? '',
        personal: {
            fullName: api.fullName ?? '',
            egn: api.idn ?? '',
            serviceNumber: api.empId ?? '',
            birthDate: formatDossierDate(api.dateOfBirth),
            age: api.age != null ? String(api.age) : '',
            birthPlace: api.birthPlace ?? '',
            birthCountry: api.country ?? '',
            nationality: api.nationality ?? '',
            gender: formatGender(api.gender),
            maritalStatus: api.familyStatus ?? '',
            childrenCount: kidsCount,
            children,
            permanentAddress: api.address ?? '',
            permanentCity: api.city ?? '',
            permanentPostalCode: api.postCode ?? '',
            permanentCountry: api.country ?? '',
            correspondenceAddress: api.correspondenceAddress ?? '',
            correspondenceCity: api.correspondenceCity ?? '',
            correspondencePostalCode: api.correspondencePostCode ?? '',
            correspondenceCountry: api.correspondenceCountry ?? '',
        },
        currentPosition: {
            company: api.branchName ?? '',
            management: api.orgUnitName ?? '',
            position: api.positionName ?? '',
            level: api.unitLevelName ?? '',
            startDate: formatDossierDate(api.effectiveDate),
            contractType: api.typeOfContract ?? '',
            manager: api.managerName ?? '',
            dailyEmployeeStatus: api.statusOfEmployee ?? '',
            titularName: api.titularName ?? '',
            deputyName: api.ocupantName ?? api.occupantName ?? '',
            nextWorkExperiencePercentChangeDate: formatDossierDate(
                api.nextWorkExperiencePercentChangeDate ?? api.nextSeniorityChangeDate
            ),
        },
        additionalExperience: {
            experiences: mapAdditionalExperienceItems(api.additionalExpirience ?? api.additionalExperience),
            remuneration: mapSalaryContracts(api.salaryContracts),
            bonusRemuneration: api.bonusRemuneration ?? '',
            coursesCurrentYear: api.coursesCurrentYear ?? '',
            coursesCurrentCompleted: api.coursesCurrentCompleted ?? '',
        },
        previousExperience: {
            education: mapEducations(api.educations),
            languages: mapLanguageSkills(api.languageSkills),
        },
    };
}
